import { z } from 'zod';
import { V } from 'helpers/validationMessages';

/**
 * The declarant record's editable fields, shared by every surface that edits
 * them: the organisation profile's Declarant Info tab and the UK registration
 * summary's inline editors (backend docs/features/uk-cbam-registration-summary
 * D16). Each surface picks its subset; one definition keeps the rules and the
 * write shape identical everywhere.
 */

/** A 5-digit UK SIC 2007 code (backend D2). */
const SIC_CODE = /^\d{5}$/;

const optional = z.string().optional().or(z.literal(''));

export const DECLARANT_FIELD_SCHEMAS = {
  declarantName: z.string().min(1, V.required),
  EORI: z.string().trim().min(1, V.required),
  contactName: optional,
  contactEmail: z.string().trim().email(V.invalidEmail).or(z.literal('')).optional(),
  country: z.string().min(1, V.required).or(z.number().transform(String)),
  state: optional,
  city: z.string().min(1, V.required).max(35, V.maxLength(35)),
  street: optional,
  streetNumber: optional,
  postcode: optional,
  additionalLine: optional,
  poBox: optional,
  // Empty clears it. Written only through PUT /uk-imports/registration-details (D3).
  sicCode: z
    .string()
    .transform((value) => value.replace(/\s/g, ''))
    .refine((value) => value === '' || SIC_CODE.test(value), V.invalid),
};

export const DECLARANT_FIELDS = {
  GENERAL: ['declarantName', 'EORI'],
  CONTACT: ['contactName', 'contactEmail'],
  ADDRESS: [
    'country',
    'state',
    'city',
    'street',
    'streetNumber',
    'postcode',
    'additionalLine',
    'poBox',
  ],
};

export const declarantSchema = (names) =>
  z.object(Object.fromEntries(names.map((name) => [name, DECLARANT_FIELD_SCHEMAS[name]])));

export function declarantDefaults(declarant, organization) {
  const addr = declarant?.address || {};
  return {
    declarantName: declarant?.name ?? organization?.name ?? '',
    EORI: declarant?.EORI ?? '',
    contactName: declarant?.contactName ?? '',
    contactEmail: declarant?.contactEmail ?? '',
    country: addr.country != null ? String(addr.country) : '',
    state: addr.state ?? '',
    city: addr.city ?? '',
    street: addr.street ?? '',
    streetNumber: addr.number ?? '',
    postcode: addr.postcode ?? '',
    additionalLine: addr.additionalLine ?? '',
    poBox: addr.POBox ?? '',
    sicCode: declarant?.sicCode ?? '',
  };
}

/** Form field → address key on the wire. */
const ADDRESS_KEY = {
  country: 'country',
  state: 'state',
  city: 'city',
  street: 'street',
  streetNumber: 'number',
  postcode: 'postcode',
  additionalLine: 'additionalLine',
  poBox: 'POBox',
};

/**
 * The `PATCH /declarant` body for ONLY the fields in `values`. Any address
 * field sends the whole address, merged over the one on file — the server
 * replaces it as a unit. `sicCode` never goes here (D3).
 */
export function declarantPatch(values, declarant) {
  const patch = {};
  if ('declarantName' in values) patch.name = values.declarantName;
  if ('EORI' in values) patch.EORI = values.EORI;
  if ('contactName' in values) patch.contactName = values.contactName;
  if ('contactEmail' in values) patch.contactEmail = values.contactEmail;
  const addressFields = Object.keys(ADDRESS_KEY).filter((field) => field in values);
  if (addressFields.length > 0) {
    patch.address = { ...(declarant?.address || {}) };
    addressFields.forEach((field) => {
      patch.address[ADDRESS_KEY[field]] = values[field];
    });
  }
  return patch;
}

const SERVER_FIELD = { name: 'declarantName', number: 'streetNumber', POBox: 'poBox' };

/**
 * A server issue's path → the form field it belongs to (`<Form serverFieldMap>`):
 * only the renamed keys — the seam already falls back to the last path segment.
 */
export const declarantServerField = (issue) =>
  Array.isArray(issue?.path) ? SERVER_FIELD[issue.path.at(-1)] : undefined;
